/**
 * Family Column Pagination Hook
 *
 * Encapsulates sliding pagination state for family member columns.
 * Uses offset-based navigation - slides 1 column at a time.
 */

import { FamilyColumnPaginationManager } from '@/lib/calendar/FamilyColumnPaginationManager';
import type { FamilyMemberResource } from '@/types/family-resource';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dimensions } from 'react-native';

const SCREEN_WIDTH = Dimensions.get('window').width;
const DEFAULT_TIME_COLUMN_WIDTH = 53;
const ARROW_BUTTON_WIDTH = 40; // Space for the right pagination arrow

type UseFamilyColumnPaginationParams = {
  resources: FamilyMemberResource[];
  timeColumnWidth?: number;
};

type UseFamilyColumnPaginationResult = {
  currentOffset: number;
  visibleResources: FamilyMemberResource[];
  columnWidth: number;
  showPagination: boolean;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
  nextPreviewResource: FamilyMemberResource | null;
  prevPreviewResource: FamilyMemberResource | null;
};

export function useFamilyColumnPagination({
  resources,
  timeColumnWidth = DEFAULT_TIME_COLUMN_WIDTH,
}: UseFamilyColumnPaginationParams): UseFamilyColumnPaginationResult {
  const [currentOffset, setCurrentOffset] = useState(0);

  const manager = useMemo(() => new FamilyColumnPaginationManager(), []);

  const visibleColumns = useMemo(() => {
    return manager.calculateVisibleColumns(SCREEN_WIDTH, timeColumnWidth);
  }, [manager, timeColumnWidth]);

  const maxOffset = useMemo(() => {
    return manager.calculateMaxOffset(resources.length, visibleColumns);
  }, [manager, resources.length, visibleColumns]);

  const showPagination = useMemo(() => {
    return manager.shouldShowPagination(resources.length, visibleColumns);
  }, [manager, resources.length, visibleColumns]);

  // Reserve space for right arrow when pagination is needed
  const availableWidth = useMemo(() => {
    const baseWidth = SCREEN_WIDTH - timeColumnWidth;
    if (manager.shouldShowPagination(resources.length, visibleColumns)) {
      return baseWidth - ARROW_BUTTON_WIDTH;
    }
    return baseWidth;
  }, [manager, timeColumnWidth, resources.length, visibleColumns]);

  const columnWidth = useMemo(() => {
    const actualVisibleCount = Math.min(visibleColumns, resources.length);
    if (actualVisibleCount === 0) return 0;
    return manager.calculateColumnWidth(availableWidth, actualVisibleCount);
  }, [manager, availableWidth, visibleColumns, resources.length]);

  const visibleResources = useMemo(() => {
    return manager.getVisibleResources(resources, currentOffset, visibleColumns);
  }, [manager, resources, currentOffset, visibleColumns]);

  // Auto-clamp offset when resources change
  useEffect(() => {
    const validOffset = manager.clampOffset(currentOffset, maxOffset);
    if (validOffset !== currentOffset) {
      setCurrentOffset(validOffset);
    }
  }, [currentOffset, maxOffset, manager]);

  const canGoPrev = manager.canGoPrev(currentOffset);
  const canGoNext = manager.canGoNext(currentOffset, maxOffset);

  // Preview resources for pagination arrows
  const nextPreviewResource = useMemo(() => {
    const nextIndex = currentOffset + visibleColumns;
    return resources[nextIndex] ?? null;
  }, [resources, currentOffset, visibleColumns]);

  const prevPreviewResource = useMemo(() => {
    const prevIndex = currentOffset - 1;
    return prevIndex >= 0 ? resources[prevIndex] : null;
  }, [resources, currentOffset]);

  const goToNextPage = useCallback(() => {
    if (canGoNext) {
      setCurrentOffset(prev => prev + 1);
    }
  }, [canGoNext]);

  const goToPrevPage = useCallback(() => {
    if (canGoPrev) {
      setCurrentOffset(prev => prev - 1);
    }
  }, [canGoPrev]);

  return {
    currentOffset,
    visibleResources,
    columnWidth,
    showPagination,
    goToNextPage,
    goToPrevPage,
    canGoNext,
    canGoPrev,
    nextPreviewResource,
    prevPreviewResource,
  };
}
